import {
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, FormikProps } from 'formik';
import { useMemo, useRef } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import * as Yup from 'yup';
import { LabelledOutline } from '../../../../common/final-form-material-ui';
import {
  FormikCheckbox,
  FormikSelect,
  FormikTextField,
} from '../../../../common/formik-material-ui';
import { trackSaveAddWebhook } from '../../../../common/userTracking';
import { endingDate } from '../../../../recoil/atoms/endingDate';
import { startingDate } from '../../../../recoil/atoms/startingDate';
import { IUserAtom, user } from '../../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../../services/rest/makeAPIRequest';
import Headers from '../../../components/Headers/Headers';
import { ModalWithStyles } from '../../../components/modals/ModalWithStyles';
import { useSnackbarWithStyles } from '../../../components/notifications/SnackbarProviderWithStyles';
import { IWebhook } from './Webhooks';

const isAnyChecked = (options: { [name: string]: boolean }[]) =>
  Array.isArray(options) &&
  options.reduce(
    (accumulator, option) => accumulator || Object.values(option)[0],
    false
  );

const isAllChecked = (options: { [name: string]: boolean }[]) =>
  Array.isArray(options) &&
  options.reduce(
    (accumulator, option) => accumulator && Object.values(option)[0],
    true
  );

const isNoneChecked = (options: { [name: string]: boolean }[]) =>
  Array.isArray(options) &&
  options.reduce(
    (accumulator, option) => accumulator && !Object.values(option)[0],
    true
  );

/**
 * Validation schema for formik
 * @see https://stackoverflow.com/questions/63058945/get-the-value-of-another-field-for-validation-in-yup-schema
 * @see https://github.com/jquense/yup#mixedtestoptions-object-schema
 * @see https://runkit.com/cashewnuts/yup-custom-max-example
 */
const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  eventname: Yup.string().required('Required'),
  location: Yup.string().required('Required').url('must be a valid URL'),
  callresult: Yup.string().test({
    name: 'required',
    exclusive: false,
    params: {},
    message: 'Call Result is required',
    test: function (value) {
      // validate if event is call
      return this.parent.eventname !== 'endcall' || Boolean(value);
    },
  }),
  dispositions: Yup.array().test({
    name: 'required',
    exclusive: false,
    params: {},
    message: 'Please select at least one call outcome',
    test: function (value) {
      return (
        this.parent.eventname !== 'disposition' ||
        isAnyChecked(value as { [name: string]: boolean }[])
      );
    },
  }),
  headers: Yup.array().of(
    Yup.object().shape(
      {
        name: Yup.string()
          .transform((curr, orig) => (orig === '' ? null : curr))
          .when(['value'], {
            is: (value, name) => !name && value,
            then: Yup.string()
              .required('Name is required')
              .matches(/^[a-zA-Z0-9_-]+$/, 'Name format is incorrect'),
          }),
        value: Yup.string()
          .transform((curr, orig) => (orig === '' ? null : curr))
          .when(['name'], {
            is: (name, value) => !value && name,
            then: Yup.string()
              .required('Value is required')
              .matches(
                //eslint-disable-next-line
                /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/,
                'Value format is incorrect'
              ),
          }),
      },
      [['value', 'name']]
    )
  ),
});

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  add: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    marginLeft: theme.spacing(1), // space between fields
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  paper: {
    maxHeight: 200,
    overflow: 'auto',
    paddingLeft: '16px',
  },
  ringScrolling: {
    '&::-webkit-scrollbar': {
      '-webkit-appearance': 'none',
      width: 7,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      backgroundColor: 'rgba(0, 0, 0, .5)',
      boxShadow: '0 0 1px rgba(255, 255, 255, .5)',
    },
    maxHeight: 200,
    paddingLeft: theme.spacing(1),
    height: 152, // height will cut off half the text to show scrollable
    overflow: 'auto',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
    height: 'max-content',
    alignSelf: 'center',
  },
  fieldSelectCol: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const contactManagementOptions = [
  'Always Use This Number',
  'Remove This Number',
  'Remove This Contact',
];

const hideDirectionEvents = [
  'scheduledactivity',
  'powerdialattempt',
  'contactmgmt',
  'powerlistsession',
];

export interface IDisposition {
  businessDispositionid: number;
  disposition: string;
  order: number;
  id: number;
}

export function WebhookAddDialog(props: {
  onHide: () => void;
  show: boolean;
  id?: number;
  webhook?: IWebhook;
}) {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  const start = useRecoilValueLoadable(startingDate);
  const end = useRecoilValueLoadable(endingDate);
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const dispositionsLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_DISPOSITION_REPORTING,
      payloadJsonString: JSON.stringify({
        startdate: start.contents,
        enddate: end.contents,
        timezone:
          businessData?.contents?.response?.data?.result?.[0]?.timezone_php,
      }),
      waitForPayload: !businessData?.contents?.response?.data?.result?.[0]
        ?.timezone_php,
    })
  );
  const selectAllDispositions = props.webhook?.dispositions === 'all';
  const selectAllContactManagement = props.webhook?.contactManagement === 'all';
  const dispositionsCached = useRef<IDisposition[]>([]);
  dispositionsCached.current = useMemo(
    () =>
      dispositionsLoadable?.contents?.data?.dispositions?.result
        ? dispositionsLoadable?.contents?.data?.dispositions?.result?.map(
            (o: any) => ({
              ...o,
              id: o.businessDispositionid,
            })
          )
        : dispositionsCached.current,
    [dispositionsLoadable]
  );
  const setRefetchToggleForWebhooks = useSetRecoilState(
    refetchToggle(endpointEnum.GET_WEBHOOKS)
  );

  const formatFieldName = (kind: string, id: string, index: number) => {
    return `${kind}.${index}.[${id}]`;
  };

  const addInitialValues = useMemo(
    () => ({
      name: '',
      location: '',
      eventname: 'endcall',
      runtime: 'realtime',
      direction: 'all',
      callresult: 'all',
      dispositions: dispositionsCached.current.map((dispo, index) => ({
        [dispo.id]: false,
        dispo: dispo.disposition,
      })),
      contactManagement: contactManagementOptions.map(
        (contactManagementItem, index) => ({
          [contactManagementItem]: false,
          contactManagementItem: contactManagementItem,
        })
      ),
      headers: [
        {
          name: '',
          value: '',
        },
      ],
    }),
    [dispositionsLoadable]
  );
  const editInitialValues = useMemo(
    () => ({
      name: props.webhook?.name ?? '',
      location: props.webhook?.webhookLocation ?? '',
      eventname: props.webhook?.event ?? '',
      runtime: props.webhook?.runtime ?? '',
      direction: props.webhook?.direction ?? '',
      callresult: props.webhook?.callresult ?? '',
      dispositions: selectAllDispositions
        ? dispositionsCached.current.map((dispo, index) => ({
            [dispo.id]: true,
            dispo: dispo.disposition,
          }))
        : dispositionsCached.current.map((dispo, index) => ({
            //making this an array with split means the includes will match the whole word
            [dispo.id]: props.webhook?.dispositions
              ?.split(',')
              .includes(dispo.disposition),
            dispo: dispo.disposition,
          })),
      contactManagement: selectAllContactManagement
        ? contactManagementOptions.map((contactManagementItem, index) => ({
            [contactManagementItem]: true,
            contactManagementItem: contactManagementItem,
          }))
        : contactManagementOptions.map((contactManagementItem, index) => ({
            [contactManagementItem]: props.webhook?.contactManagement
              ?.split(',')
              .includes(contactManagementItem),
            contactManagementItem: contactManagementItem,
          })),
      headers: props.webhook?.headers ?? [
        {
          name: '',
          value: '',
        },
      ],
    }),
    [props.webhook, dispositionsLoadable]
  );

  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));

  return (
    <Paper>
      <Formik
        enableReinitialize={true}
        initialValues={props.id ? editInitialValues : addInitialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          const {
            name,
            location,
            eventname,
            runtime,
            direction,
            callresult,
            dispositions,
            contactManagement,
            headers,
          } = values;
          const filteredHeaders = headers.filter(
            (header) => header.name !== '' && header.value !== ''
          );
          const response = await mutation.mutateAsync({
            endpoint: props.id
              ? endpointEnum.PUT_WEBHOOK
              : endpointEnum.POST_WEBHOOK,
            payload: {
              webhookid: props.id,
              call: props.id
                ? methodNameEnum.PUT_WEBHOOK
                : methodNameEnum.POST_WEBHOOK,
              name,
              eventname,
              location,
              runtime,
              direction,
              callresult,
              disposition: isAllChecked(dispositions as any)
                ? 'all'
                : dispositions
                    .filter((item) => Object.values(item)[0])
                    .map((item) => item.dispo)
                    .join(','),
              contactManagement: isAllChecked(contactManagement as any)
                ? 'all'
                : contactManagement
                    .filter((item) => Object.values(item)[0])
                    .map((item) => item.contactManagementItem)
                    .join(','),
              headers:
                filteredHeaders.length > 0 && JSON.stringify(filteredHeaders),
            },
          });
          const resSuccess = response?.data?.success;
          const resMessage = response?.data?.result?.code;
          if (!resSuccess) {
            enqueueSnackbarWithStyles({
              message:
                resMessage === 'ER_DUP_ENTRY'
                  ? 'Please choose a Webhook Name that is not already in use'
                  : `Encountered an error trying to add ${name} Webhook`,
              alertProps: {
                severity: 'error',
              },
            });
            return;
          }
          if (response.status === 200) {
            enqueueSnackbarWithStyles({
              message: `Succesfully ${
                props.id ? 'Updated' : 'Added'
              } ${name} Webhook!`,
              alertProps: {
                severity: 'success',
              },
            });
          } else {
            enqueueSnackbarWithStyles({
              message: `Entcountered an Error ${
                props.id ? 'Updating' : 'Adding'
              } ${name} Webhook.`,
              alertProps: {
                severity: 'warning',
              },
            });
          }
          props.onHide();
          setRefetchToggleForWebhooks({});
        }}
      >
        {(formik: FormikProps<any>) => {
          return (
            <ModalWithStyles
              onSubmit={() => {
                trackSaveAddWebhook();
                formik.handleSubmit();
              }}
              title={props.id ? 'Edit Webhook' : 'Add New Webhook'}
              aria-labelledby="example-modal-sizes-title-lg"
              show={props.show}
              onHide={props.onHide}
              size="md"
              isSubmitting={formik.isSubmitting}
              isLoading={
                formik.isSubmitting || dispositionsLoadable.state === 'loading'
              }
              footerComponent={
                <div>
                  <Button
                    className={classes.button}
                    variant="contained"
                    color="inherit"
                    onClick={() => {
                      props.onHide();
                    }}
                    disableElevation
                  >
                    Close
                  </Button>
                  <Button
                    className={classes.add}
                    variant="contained"
                    data-qa="webhook-save-button"
                    onClick={formik.handleSubmit as any}
                  >
                    Save Changes
                  </Button>
                </div>
              }
            >
              <form>
                <div className="container my-5">
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Name</h5>
                      <p className="fieldDescription">
                        Add a name for this webhook to help you identify it.
                        Make it descriptive enough for easy recognition in the
                        system.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikTextField
                        name="name"
                        className={classes.field}
                        formik={formik}
                        fullWidth
                        label="Name"
                        inputProps={{ maxLength: 150 }}
                        margin="dense"
                        size="small"
                        required
                        variant="outlined"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Webhook Url</h5>
                      <p className="fieldDescription">
                        Enter the URL this webhook will send data to when
                        triggered. Make sure the endpoint is properly configured
                        to receive webhook data.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikTextField
                        name="location"
                        className={classes.field}
                        formik={formik}
                        // autoFocus={true}
                        fullWidth
                        label="Webhook Url"
                        inputProps={{ maxLength: 500 }}
                        margin="dense"
                        size="small"
                        required
                        variant="outlined"
                      />
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Event Name</h5>
                      <p className="fieldDescription">
                        Choose the event that will trigger this webhook. Some
                        events present additional customization properties.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikSelect
                        name="eventname"
                        className={classes.field}
                        formik={formik}
                        fullWidth
                        label="Event Name"
                        margin="dense"
                        size="small"
                        variant="outlined"
                        required
                      >
                        <MenuItem value="endcall">End Call</MenuItem>
                        <MenuItem value="startcall">Start Call</MenuItem>
                        <MenuItem value="answeredcall">Answered Call</MenuItem>
                        <MenuItem value="sms">SMS</MenuItem>
                        <MenuItem value="disposition">Call Outcome</MenuItem>
                        <MenuItem value="voicemail">Voicemail</MenuItem>
                        <MenuItem value="scheduledactivity">
                          Scheduled Activity
                        </MenuItem>
                        <MenuItem value="cisummary">CI Summary</MenuItem>
                        {/* <MenuItem value="contactmgmt">
                          Contact Management
                        </MenuItem> */}
                        <MenuItem value="powerdialattempt">
                          PowerDial Attempts
                        </MenuItem>
                        <MenuItem value="powerlistsession">
                          Agent PowerDial Session
                        </MenuItem>
                      </FormikSelect>
                    </div>
                  </div>
                  <div className="row borderBottom">
                    <div className="col-lg-8">
                      <h5 className="fieldTitle">Runtime</h5>
                      <p className="fieldDescription">
                        Select whether the webhook should run in real-time or
                        batch mode. Real-time ensures immediate triggering,
                        while batch mode groups actions for later processing.
                      </p>
                    </div>
                    <div className="col-lg-4">
                      <FormikSelect
                        name="runtime"
                        className={classes.field}
                        formik={formik}
                        fullWidth
                        label="Runtime"
                        margin="dense"
                        size="small"
                        variant="outlined"
                      >
                        <MenuItem value="realtime">Realtime</MenuItem>
                        {formik.values.eventname === 'endcall' && (
                          <MenuItem value="batch">Batch</MenuItem>
                        )}
                      </FormikSelect>
                    </div>
                  </div>
                  {!hideDirectionEvents.includes(formik.values.eventname) && (
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Direction</h5>
                        <p className="fieldDescription">
                          Specify the direction of the call or action that this
                          webhook applies to. Options include incoming,
                          outgoing, or all directions.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormikSelect
                          name="direction"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          label="Direction"
                          margin="dense"
                          size="small"
                          variant="outlined"
                        >
                          {formik.values.eventname !== 'voicemail' && (
                            <MenuItem value="all">All</MenuItem>
                          )}
                          <MenuItem value="incoming">Incoming</MenuItem>
                          {formik.values.eventname !== 'voicemail' && (
                            <MenuItem value="outgoing">Outgoing</MenuItem>
                          )}
                        </FormikSelect>
                      </div>
                    </div>
                  )}
                  {formik.values.eventname === 'endcall' && (
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Call Result</h5>
                        <p className="fieldDescription">
                          Trigger this webhook when a call ends. Choose between
                          all (any call ends), Missed (only triggers when an
                          unanswered call ends), or Answered (only triggers when
                          an answered call ends).
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <FormikSelect
                          name="callresult"
                          className={classes.field}
                          formik={formik}
                          required={formik.values.eventname === 'endcall'}
                          fullWidth
                          label="Call Result"
                          margin="dense"
                          size="small"
                          variant="outlined"
                        >
                          {formik.values.eventname !== 'answeredcall' && (
                            <MenuItem value="all">All</MenuItem>
                          )}
                          <MenuItem value="answered">Answered</MenuItem>
                          {formik.values.eventname !== 'answeredcall' && (
                            <MenuItem value="missed">Missed</MenuItem>
                          )}
                        </FormikSelect>
                      </div>
                    </div>
                  )}
                  {formik.values.eventname === 'disposition' &&
                    formik.values.dispositions.length && (
                      <div className="row borderBottom">
                        <div className="col-lg-8">
                          <h5 className="fieldTitle">Call Outcomes</h5>
                          <p className="fieldDescription">
                            Select the call outcome that will be associated with
                            this webhook. These determine which outcomes will
                            trigger the webhook when applied.
                          </p>
                        </div>
                        <div className="col-lg-4">
                          <LabelledOutline
                            label="Call Outcomes"
                            error={Boolean(formik.errors.dispositions)}
                            errorText={String(formik.errors.dispositions)}
                            selectAllOnClick={(event) => {
                              // uncheck if checked and vice-versa
                              formik.values.dispositions.forEach(
                                (
                                  disposition: { [name: string]: boolean },
                                  index: number
                                ) => {
                                  formik.setFieldValue(
                                    formatFieldName(
                                      'dispositions',
                                      Object.keys(disposition)[0],
                                      index
                                    ),
                                    !isAllChecked(formik.values.dispositions)
                                  );
                                }
                              );
                            }}
                            selectAllIsAllChecked={isAllChecked(
                              formik.values.dispositions
                            )}
                            selectAllIsNoneChecked={isNoneChecked(
                              formik.values.dispositions
                            )}
                          >
                            <div className={classes.ringScrolling}>
                              {dispositionsCached.current.map(
                                (dispo, index) => (
                                  <FormControlLabel
                                    style={{ display: 'flex' }}
                                    key={index}
                                    control={
                                      <FormikCheckbox
                                        required={
                                          formik.values.eventname ===
                                          'disposition'
                                        }
                                        formik={formik}
                                        name={formatFieldName(
                                          'dispositions',
                                          dispo.id.toString(),
                                          index
                                        )}
                                        color="primary"
                                      />
                                    }
                                    label={dispo.disposition}
                                  />
                                )
                              )}
                            </div>
                          </LabelledOutline>
                        </div>
                      </div>
                    )}
                  {formik.values.eventname === 'contactmgmt' && (
                    <div className="row borderBottom">
                      <div className="col-lg-8">
                        <h5 className="fieldTitle">Action</h5>
                        <p className="fieldDescription">
                          Trigger this webhook when a contact management action
                          is taken. Choose between all (contact management
                          action), always use this number, remove this number,
                          or remove this contact.
                        </p>
                      </div>
                      <div className="col-lg-4">
                        <LabelledOutline
                          label="Contact Management"
                          error={Boolean(formik.errors.contactManagement)}
                          errorText={String(formik.errors.contactManagement)}
                          selectAllOnClick={(event) => {
                            // uncheck if checked and vice-versa
                            formik.values.contactManagement.forEach(
                              (
                                contactManagement: { [name: string]: boolean },
                                index: number
                              ) => {
                                formik.setFieldValue(
                                  formatFieldName(
                                    'contactManagement',
                                    Object.keys(contactManagement)[0],
                                    index
                                  ),
                                  !isAllChecked(formik.values.contactManagement)
                                );
                              }
                            );
                          }}
                          selectAllIsAllChecked={isAllChecked(
                            formik.values.contactManagement
                          )}
                          selectAllIsNoneChecked={isNoneChecked(
                            formik.values.contactManagement
                          )}
                        >
                          <div className={classes.ringScrolling}>
                            {contactManagementOptions.map(
                              (contactManagementItem, index) => (
                                <FormControlLabel
                                  style={{ display: 'flex' }}
                                  key={index}
                                  control={
                                    <FormikCheckbox
                                      required={
                                        formik.values.eventname ===
                                        'contactManagement'
                                      }
                                      formik={formik}
                                      name={formatFieldName(
                                        'contactManagement',
                                        contactManagementItem,
                                        index
                                      )}
                                      color="primary"
                                    />
                                  }
                                  label={contactManagementItem}
                                />
                              )
                            )}
                          </div>
                        </LabelledOutline>
                      </div>
                    </div>
                  )}
                  <div className="col-lg-8 px-0">
                    <h5 className="fieldTitle">Headers</h5>
                    <p className="fieldDescription">
                      Optionally, specify any headers that need to be sent along
                      with the webhook request. This is typically used for
                      authentication or additional data formatting.
                    </p>
                  </div>
                  <Headers formik={formik} />
                </div>
              </form>
            </ModalWithStyles>
          );
        }}
      </Formik>
    </Paper>
  );
}
